import React from 'react';
import './paperwork.css';
import { COMPANY } from '../../constants/company';

const Header = () => (
  <div className="flex items-start justify-between">
    <div className="flex items-center gap-2">
      <svg viewBox="0 0 40 40" className="w-10 h-10">
        <circle cx="20" cy="20" r="20" fill="#1F0068" />
        <path d="M12 22 L18 28 L28 14" fill="none" stroke="#FF5C39" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <div className="leading-tight">
        <div className="font-bold text-[15px] text-[#1F0068]">{COMPANY.shortName}</div>
        <div className="text-[9px] tracking-[0.22em] text-[#FF5C39] uppercase font-semibold">Renewal Notice</div>
      </div>
    </div>
    <div className="text-right text-[10px] text-[#1F0068]/70 leading-relaxed">
      <div>{COMPANY.phone}</div>
      <div>{COMPANY.email}</div>
      <div>{COMPANY.registeredOffice.full}</div>
    </div>
  </div>
);

const RenewalLetter = ({ policy }) => {
  const fullName = `${policy.title || ''} ${policy.firstName} ${policy.lastName}`.trim();
  const renewalDate = new Date(policy.policyEnd);
  const newStart = new Date(renewalDate);
  const newEnd = new Date(renewalDate);
  newEnd.setFullYear(newEnd.getFullYear() + 1);
  const issued = new Date();
  return (
    <div className="a4-doc">
      <div className="a4-brand-strip" />
      <Header />

      <div className="mt-8 grid grid-cols-[1fr_auto] gap-4 items-start">
        <div className="text-[11px] text-[#1F0068]/80">
          <div className="font-bold text-[#1F0068]">{fullName}</div>
          <div>{policy.address1}</div>
          {policy.address2 && <div>{policy.address2}</div>}
          <div>{policy.city}</div>
          <div>{policy.postcode}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[10px]">
          <div className="bg-[#FFF6EB] rounded-lg p-2 text-center">
            <div className="uppercase tracking-widest text-[#1F0068]/60 font-bold text-[9px]">Issue Date</div>
            <div className="font-bold text-[#1F0068]">{issued.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          </div>
          <div className="bg-[#FFEDD6] rounded-lg p-2 text-center">
            <div className="uppercase tracking-widest text-[#1F0068]/60 font-bold text-[9px]">Renewal Date</div>
            <div className="font-bold text-[#1F0068]">{renewalDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-[13px] text-[#1F0068]/85">Dear {policy.firstName},</div>
        <h1 className="mt-2 text-3xl font-extrabold leading-tight">Time to renew your <span className="text-[#FF5C39]">Maintenance Cover Plan</span></h1>
        <p className="mt-4 text-[12px] leading-relaxed text-[#1F0068]/85">
          Thank you for being a valued customer of {COMPANY.name}. This is a friendly reminder that your Maintenance Cover Plan {policy.planNumber} is due to renew on <b>{renewalDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</b>. To keep your appliances continuously protected, please arrange payment before this date.
        </p>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-4">
        <div className="bg-[#FFF6EB] rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#1F0068]/60 font-bold">Your renewal at a glance</div>
          <div className="mt-2 text-[11px] text-[#1F0068]/85 space-y-1">
            <div><span className="text-[#1F0068]/60">Plan number:</span> <b className="text-[#1F0068]">{policy.planNumber}</b></div>
            <div><span className="text-[#1F0068]/60">Cover period:</span> <b className="text-[#1F0068]">{newStart.toLocaleDateString('en-GB')} – {newEnd.toLocaleDateString('en-GB')}</b></div>
            <div><span className="text-[#1F0068]/60">Annual premium:</span> <b className="text-[#1F0068]">£ {Number(policy.amount || 0).toFixed(2)}</b></div>
            <div><span className="text-[#1F0068]/60">Products covered:</span> <b className="text-[#1F0068]">{policy.appliances.join(', ')}</b></div>
          </div>
        </div>
        <div className="bg-[#1F0068] text-white rounded-xl p-4">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5C39] font-bold">How to pay</div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            <div><div className="text-white/70 text-[10px]">Account Name</div><div className="font-bold">{COMPANY.bank.accountName}</div></div>
            <div><div className="text-white/70 text-[10px]">Sort Code</div><div className="font-bold">{COMPANY.bank.sortCode}</div></div>
            <div><div className="text-white/70 text-[10px]">Account Number</div><div className="font-bold">{COMPANY.bank.accountNumber}</div></div>
            <div><div className="text-white/70 text-[10px]">Reference</div><div className="font-bold">{policy.planNumber}</div></div>
          </div>
          <p className="mt-2 text-[10px] text-white/70 italic">Payments accepted by Direct Bank Transfer only.</p>
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#1F0068]/10 rounded-xl p-3 text-[11px] text-[#1F0068]/85">
        <b className="text-[#1F0068]">Not for you this time?</b> That’s okay — simply reply to this letter or call us on {COMPANY.phoneShort} and we’ll close your plan on the renewal date. You have the right to cancel at any time, and we won’t take payment without your approval.
      </div>

      <div className="mt-6 text-[12px] text-[#1F0068]/85">
        <div>Yours sincerely,</div>
        <div className="mt-1" style={{ fontFamily: 'cursive', fontSize: '18px', color: '#1F0068' }}>Team {COMPANY.shortName}</div>
        <div className="text-[10px] text-[#1F0068]/60">Customer Renewals Team</div>
      </div>

      <div className="a4-footer">
        <div>{COMPANY.name} · {COMPANY.registeredOffice.full}</div>
        <div>Company No. {COMPANY.companyNumber} · Renewal for Plan {policy.planNumber}</div>
      </div>
    </div>
  );
};

export default RenewalLetter;
